/** Форматирование для интерфейса. Всё в одном месте, чтобы «500 ₽» везде выглядело одинаково. */
import type { VideoStatus } from './types';

/** Деньги приходят с сервера в копейках */
export function money(kopecks: number): string {
  const rub = Math.abs(kopecks) / 100;
  return (
    (kopecks < 0 ? '−' : '') +
    rub.toLocaleString('ru-RU', { maximumFractionDigits: rub % 1 === 0 ? 0 : 2 }) +
    ' ₽'
  );
}

export const moneySigned = (kopecks: number) => (kopecks > 0 ? '+' : '') + money(kopecks);

export function bytes(value: number | string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n >= 1024 ** 4) return (n / 1024 ** 4).toFixed(2) + ' ТБ';
  if (n >= 1024 ** 3) return (n / 1024 ** 3).toFixed(1) + ' ГБ';
  if (n >= 1024 ** 2) return (n / 1024 ** 2).toFixed(0) + ' МБ';
  if (n >= 1024) return (n / 1024).toFixed(0) + ' КБ';
  return n + ' Б';
}

export function duration(sec: number | null): string {
  if (!sec) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

/** Таймкод в замечаниях: 2:14 */
export const timecode = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

export function dateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function date(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/**
 * «осталось 3 ч» или «просрочено на 2 д».
 * Показываем именно так, а не голой датой: человеку важен остаток, а не момент.
 */
export function untilDeadline(iso: string | null): { text: string; overdue: boolean } {
  if (!iso) return { text: '—', overdue: false };

  const diff = new Date(iso).getTime() - Date.now();
  const overdue = diff < 0;
  const abs = Math.abs(diff);

  const days = Math.floor(abs / 86_400_000);
  const hours = Math.floor((abs % 86_400_000) / 3_600_000);
  const mins = Math.floor((abs % 3_600_000) / 60_000);

  const body = days > 0 ? `${days} д ${hours} ч` : hours > 0 ? `${hours} ч ${mins} мин` : `${mins} мин`;

  return { text: overdue ? `просрочено на ${body}` : `осталось ${body}`, overdue };
}

/** Секунды в «7 мин» — для оставшегося времени загрузки */
export function eta(sec: number | null): string {
  if (sec === null) return '—';
  if (sec < 60) return `${sec} сек`;
  if (sec < 3600) return `${Math.ceil(sec / 60)} мин`;
  return `${Math.floor(sec / 3600)} ч ${Math.round((sec % 3600) / 60)} мин`;
}

export const STATUS: Record<VideoStatus, { label: string; className: string }> = {
  NEW: { label: 'Новое', className: 'bg-info/10 text-info' },
  SHOOTING: { label: 'Съёмка', className: 'bg-accent/12 text-accent2' },
  AWAITING_EDIT: { label: 'Ждёт монтаж', className: 'bg-info/10 text-info' },
  EDITING: { label: 'Монтаж', className: 'bg-accent/12 text-accent2' },
  REVIEW: { label: 'На проверке', className: 'bg-warn/10 text-warn' },
  REVISION: { label: 'Доработка', className: 'bg-dang/10 text-dang' },
  DONE: { label: 'Готово', className: 'bg-ok/10 text-ok' },
  CANCELLED: { label: 'Отменено', className: 'bg-white/5 text-muted' },
  PUBLISHED: { label: 'Опубликовано', className: 'bg-ok/10 text-ok' },
};

/** Человеческие названия типов файлов. Служебные показываем только там, где нужно. */
export const FILE_KIND: Record<string, { label: string; icon: string; internal?: boolean }> = {
  MATERIAL: { label: 'Исходники', icon: '🗜' },
  FINAL: { label: 'Готовое видео', icon: '🎬' },
  COVER: { label: 'Обложка', icon: '🖼' },
  PROXY: { label: 'Превью для просмотра', icon: '◫', internal: true },
  THUMB: { label: 'Кадр из видео', icon: '▣', internal: true },
};

/** Цвет аватара выводим из id — одинаковый у человека везде, хранить не нужно */
export function avatarColor(id: string): string {
  const palette = ['#7C5CFF', '#4F8AF5', '#E0705A', '#2FAF7E', '#C58A2E', '#B5539E', '#3EA8C4'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length]!;
}

/**
 * Склонение существительного по числу: 1 работа, 2 работы, 5 работ.
 *
 * Без этого в интерфейсе появляются перлы вроде «1 готовых работ»,
 * которые сразу выдают, что текст собирали склейкой.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = n % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

/** «Иванов Иван» → «ИИ» */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}
